import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Modal, Button, Collapse } from "react-bootstrap";
import plist from "plist";
import Spinner from "../components/Spinner";
import NotFound from "./NotFound";
import ConfigProfileRow from "../components/ConfigProfileRow.jsx";
import { FaPlusCircle, FaChevronDown, FaChevronRight } from "react-icons/fa";
import isAdministrator from "../utilities/checkPrivileges";
import InstallProfileModal from "../components/modals/InstallProfileModal.jsx";
import { GET_CONFIG_PROFILES } from "../queries/configProfilesQueries.js";
import profileService from "../services/profileService.js";
import nanoCmdService from "../services/nanoCmdService.js";
import { toast } from "react-toastify";

/** Base64 → UTF-8-String für Plist/XML */
function base64ToUtf8(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

/** UTF-8-String → Base64 */
function utf8ToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Apple: PayloadUUID muss global eindeutig sein; bei Profiländerung neue UUIDs (vgl. TopLevel-Payload). */
function randomPayloadUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function assignNewPayloadUUIDs(parsed) {
  if (!parsed || typeof parsed !== "object") return;
  parsed.PayloadUUID = randomPayloadUUID();
  if (Array.isArray(parsed.PayloadContent)) {
    for (const item of parsed.PayloadContent) {
      if (item && typeof item === "object") {
        item.PayloadUUID = randomPayloadUUID();
      }
    }
  }
}

function ProfileContentModal({ visible, profile, onClose }) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !profile) {
      setContent("");
      return;
    }
    if (profile.isNanoCmd && profile.name) {
      setLoading(true);
      nanoCmdService.getNanoCmdProfileContent(profile.name)
        .then((base64) => {
          try {
            setContent(atob(base64 || ""));
          } catch {
            setContent("(nicht lesbar)");
          }
        })
        .catch(() => setContent("(Fehler beim Laden)"))
        .finally(() => setLoading(false));
    } else {
      try {
        setContent(atob(profile.MobileConfigData || "") || "(leer)");
      } catch {
        setContent(profile.MobileConfigData || "(nicht lesbar)");
      }
    }
  }, [visible, profile]);

  if (!profile) return null;
  const title = profile.isNanoCmd ? profile.name : profile.PayloadDisplayName;

  return (
    <Modal show={visible} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Profil-Inhalt: {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner />
        ) : (
          <pre className="bg-light p-3 small overflow-auto" style={{ maxHeight: "60vh" }}>
            {content}
          </pre>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Schließen</Button>
      </Modal.Footer>
    </Modal>
  );
}

function ProfileEditModal({ visible, profile, onClose, onSaved }) {
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [showPlaceholderDocs, setShowPlaceholderDocs] = useState(false);

  useEffect(() => {
    if (!visible || !profile) {
      setEditedContent("");
      setIsSigned(false);
      return;
    }
    if (profile.isNanoCmd && profile.name) {
      setLoading(true);
      nanoCmdService.getNanoCmdProfileContent(profile.name)
        .then((raw) => {
          if (!raw) return;
          try {
            const decoded = base64ToUtf8(raw);
            plist.parse(decoded);
            setEditedContent(decoded);
          } catch {
            setIsSigned(true);
          }
        })
        .catch(() => setIsSigned(true))
        .finally(() => setLoading(false));
    } else {
      const raw = profile.MobileConfigData || "";
      if (!raw) return;
      try {
        const decoded = base64ToUtf8(raw);
        plist.parse(decoded);
        setEditedContent(decoded);
      } catch {
        setIsSigned(true);
      }
    }
  }, [visible, profile]);

  if (!profile) return null;
  const title = profile.isNanoCmd ? profile.name : profile.PayloadDisplayName;
  const isNanoCmd = !!profile.isNanoCmd;

  const handleSave = async () => {
    if (isSigned) return;
    if (!isNanoCmd && !profile._id) return;
    setSaving(true);
    try {
      const parsed = plist.parse(editedContent);
      if (!parsed || typeof parsed !== "object") {
        toast.error("Ungültiger Plist-Inhalt.");
        return;
      }
      // Apple TopLevel: PayloadVersion = Profilformat (immer 1). Updates: neue PayloadUUID(s), nicht Version hochzählen.
      if (!isNanoCmd) {
        parsed.PayloadVersion = 1;
        assignNewPayloadUUIDs(parsed);
      }
      const built = plist.build(parsed);
      const encoded = utf8ToBase64(built);
      if (isNanoCmd) {
        await nanoCmdService.updateNanoCmdProfile(profile.name, encoded);
        toast.success("NanoCMD-Profil aktualisiert.");
      } else {
        await profileService.updateProfile(profile._id, { MobileConfigData: encoded });
        toast.success("Profil gespeichert. Neue PayloadUUID(s) – Geräte installieren beim nächsten Profile List Update.");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.message?.includes("parse") ? "Ungültiger Plist-Inhalt." : err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={visible} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Profil bearbeiten: {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner />
        ) : isSigned ? (
          <p className="text-warning">
            Dieses Profil ist signiert und kann nicht bearbeitet werden. Signierte Profile bleiben unverändert.
          </p>
        ) : (
          <>
            <p className="text-muted small">
              Plist/XML-Inhalt anpassen. Beim Speichern bleibt PayloadVersion 1 (Apple-Profilformat); es werden neue PayloadUUIDs gesetzt – Geräte erhalten das aktualisierte Profil beim nächsten Profile List Update.
            </p>
            <div className="mb-3">
              <button
                type="button"
                className="placeholder-docs-toggle"
                onClick={() => setShowPlaceholderDocs((v) => !v)}
                aria-expanded={showPlaceholderDocs}
              >
                {showPlaceholderDocs ? <FaChevronDown className="me-1" /> : <FaChevronRight className="me-1" />}
                Platzhalter in Profilen
              </button>
              <Collapse in={showPlaceholderDocs}>
                <div className="mt-2">
                  <div className="small text-muted mb-1">
                    Diese Platzhalter werden beim Installieren durch Geräte-/Registrierungsdaten ersetzt:
                  </div>
                  <table className="table table-sm table-bordered" style={{ maxWidth: "600px" }}>
                    <thead>
                      <tr>
                        <th>Platzhalter</th>
                        <th>Ersetzt durch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PLACEHOLDER_DOCS.map(({ placeholder, beschreibung }) => (
                        <tr key={placeholder}>
                          <td className="font-monospace">{placeholder}</td>
                          <td>{beschreibung}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
            <textarea
              className="form-control font-monospace small"
              rows={18}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              spellCheck={false}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{isSigned ? "Schließen" : "Abbrechen"}</Button>
        {!isSigned && (
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

const PLACEHOLDER_DOCS = [
  { placeholder: "{{FULLNAME}}", beschreibung: "Gerätename (plannedDeviceName oder DeviceName)" },
  { placeholder: "{{ASSETTAG}}", beschreibung: "Asset-Tag" },
  { placeholder: "{{SERIALNUMBER}}", beschreibung: "Seriennummer" },
  { placeholder: "{{DEVICEUUID}}", beschreibung: "UDID des Geräts" },
  { placeholder: "{{LOCATION-ID}}", beschreibung: "ObjectId der Location" },
  { placeholder: "{{LOCATION-NAME}}", beschreibung: "Name der Location" },
  { placeholder: "{{NOTES}}", beschreibung: "Notizen aus der Geräteregistrierung" },
];

function Profiles() {
  const [showInstallProfileModal, setShowInstallProfileModal] = useState(false);
  const [profileForContent, setProfileForContent] = useState(null);
  const [profileForEdit, setProfileForEdit] = useState(null);
  const [showPlaceholderDocs, setShowPlaceholderDocs] = useState(false);
  const [nanocmdProfiles, setNanocmdProfiles] = useState([]);
  const [nanocmdAvailable, setNanocmdAvailable] = useState(false);

  const { loading, error, data, refetch } = useQuery(GET_CONFIG_PROFILES);

  useEffect(() => {
    nanoCmdService.getStatus().then((r) => {
      setNanocmdAvailable(r?.available ?? false);
      if (r?.available) nanoCmdService.getProfiles().then((p) => setNanocmdProfiles(p?.profiles ?? []));
    }).catch(() => setNanocmdAvailable(false));
  }, []);

  const displayInstallProfileModal = () => setShowInstallProfileModal(true);
  const hideInstallProfileModal = () => setShowInstallProfileModal(false);

  const handleViewContent = (profile) => setProfileForContent(profile);
  const handleEdit = (profile) => setProfileForEdit(profile);
  const handleDelete = async (profile) => {
    if (!profile._id) return;
    if (!window.confirm(`Profil "${profile.PayloadDisplayName}" wirklich löschen?`)) return;
    try {
      await profileService.deleteProfile(profile._id);
      toast.success("Profil gelöscht.");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Löschen fehlgeschlagen.");
    }
  };

  const isAdmin = isAdministrator();

  if (loading) return <Spinner />;
  if (error) return <NotFound />;
  if (!data?.configProfiles) return <Spinner />;

  return (
    <>
      <main>
        <div className="header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h1>Config Profile Library</h1>
          {isAdmin && (
            <Link to="/profiles/editor" className="btn btn-primary btn-sm">Neues Profil im Editor</Link>
          )}
        </div>
        <div className="mb-3">
          <button
            type="button"
            className="placeholder-docs-toggle"
            onClick={() => setShowPlaceholderDocs((v) => !v)}
            aria-expanded={showPlaceholderDocs}
          >
            {showPlaceholderDocs ? <FaChevronDown className="me-1" /> : <FaChevronRight className="me-1" />}
            Platzhalter in Profilen
          </button>
          <Collapse in={showPlaceholderDocs}>
            <div className="mt-2">
              <div className="small text-muted mb-1">
                Diese Platzhalter werden beim Installieren durch Geräte-/Registrierungsdaten ersetzt:
              </div>
              <table className="table table-sm table-bordered" style={{ maxWidth: "600px" }}>
                <thead>
                  <tr>
                    <th>Platzhalter</th>
                    <th>Ersetzt durch</th>
                  </tr>
                </thead>
                <tbody>
                  {PLACEHOLDER_DOCS.map(({ placeholder, beschreibung }) => (
                    <tr key={placeholder}>
                      <td className="font-monospace">{placeholder}</td>
                      <td>{beschreibung}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Collapse>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Organization</th>
              <th>Version</th>
              {isAdmin && <th className="text-end">Aktionen</th>}
            </tr>
          </thead>
          <tbody>
            {data.configProfiles.map((profile) => (
              <ConfigProfileRow
                key={profile.PayloadIdentifier}
                profile={profile}
                isAdmin={isAdmin}
                onView={handleViewContent}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
            {nanocmdAvailable && nanocmdProfiles.length > 0 && nanocmdProfiles.map((p) => (
              <ConfigProfileRow
                key={`nanocmd-${p.name}`}
                profile={p}
                isAdmin={isAdmin}
                isNanoCmd
                onView={handleViewContent}
                onEdit={handleEdit}
              />
            ))}
          </tbody>
        </table>
        <div>
          {isAdmin ? (
            showInstallProfileModal ? (
              <InstallProfileModal
                visible={showInstallProfileModal}
                UDID={null}
                currentProfiles={null}
                configProfiles={data.configProfiles}
                hideInstallProfileModal={hideInstallProfileModal}
              />
            ) : (
              <button className="btn" onClick={displayInstallProfileModal}>
                <FaPlusCircle />
              </button>
            )
          ) : null}
        </div>
      </main>

      <ProfileContentModal
        visible={!!profileForContent}
        profile={profileForContent}
        onClose={() => setProfileForContent(null)}
      />
      <ProfileEditModal
        visible={!!profileForEdit}
        profile={profileForEdit}
        onClose={() => setProfileForEdit(null)}
        onSaved={() => refetch()}
      />
    </>
  );
}

export default Profiles;
